import * as trace from 'owa-trace';
import CampaignIrisRemoteSource from '../store/CampaignIrisRemoteSource';
import { ARC_API_CALL_COMPLETED, campaignEventEmitter } from './campaignEventEmitters';
import { CampaignBanner } from '../campaign-types/CampaignBanner';
import { CampaignConfig } from '../store/schema/CampaignConfig';
import { CampaignEmbeddedMessage } from '../campaign-types/CampaignEmbeddedMessage';
import { CampaignModal } from '../campaign-types/CampaignModal';
import { CampaignOpxHost } from '../campaign-types/CampaignOpxHost';
import { CampaignOverlayBanner } from '../campaign-types/CampaignOverlayBanner';
import { CampaignOverlayChild } from '../campaign-types/CampaignOverlayChild';
import { CampaignSnackBar } from '../campaign-types/CampaignSnackBar';
import { CampaignTooltip } from '../campaign-types/CampaignTooltip';
import { CampaignType } from '../store/schema/CampaignType';
import { format } from 'owa-strings/utilities/format';
import { hasQueryStringParameter } from 'owa-querystring';
import { makeArcCall } from 'mini-iris-gateway';

/**
 * Utility to extract all active campaigns present in IRIS for a surface and return the campaign configs of the same
 * @param placementId This is the id to identity the surface. Also known as placement id
 * @param realTimeSignals This is the realtime signals which are required to pass on to IRIS via arc api
 * Sample uri for arc - https://arc.msn.com/v4/api/selection?placement=88000517&country=US&pl=EN-US&bcnt=100&nct=1&puid=10037FFEA929F43C&newUser=true
 */
export const getIrisActiveCampaignConfigs = async (
    placementId: string,
    realTimeSignals: string
): Promise<CampaignConfig[]> => {
    let activeCampaignConfigs: CampaignConfig[] = [];
    let isIrisCallSuccess = true;
    if (hasQueryStringParameter('mock-campaigns')) {
        const campaignConfigs: CampaignConfig[] = (
            await import('./getMockIrisCampaigns')
        ).getMockIrisCampaigns(placementId);
        activeCampaignConfigs = campaignConfigs ? campaignConfigs : [];
    } else {
        try {
            const result = await makeArcCall(placementId, realTimeSignals);
            const campaigns = await result.value;
            campaigns?.batchrsp?.items?.forEach(item => {
                const itemJSON = JSON.parse(item?.item);
                const creative = itemJSON.ad;
                const impression = itemJSON.prm._imp;
                const beacon = itemJSON.tracking.baseUri;
                const campaignConfig: CampaignConfig = {
                    rule: () => Promise.resolve(true),
                    remoteCampaignId: creative.campaignId,
                    campaignType: getCampaignTypeObj(creative.component),
                    campaignSource: new CampaignIrisRemoteSource(beacon, impression),
                    renderProps: creative,
                };
                activeCampaignConfigs.push(campaignConfig);
            });
            isIrisCallSuccess = result.isCallSuccess;
        } catch (error) {
            trace.errorThatWillCauseAlert(
                `Error in parsing the campaign creative from IRIS for placementID ${placementId}'. Error:  ${error.message}`,
                error
            );
            isIrisCallSuccess = false;
        }
    }
    campaignEventEmitter.emit(format(ARC_API_CALL_COMPLETED, placementId), {
        isCampaignActive: activeCampaignConfigs.length > 0,
        isIrisCallSuccess: isIrisCallSuccess,
    });
    return activeCampaignConfigs;
};

const getCampaignTypeObj = (component: string): CampaignType<any> => {
    switch (component.toLowerCase()) {
        case 'embeddedmessage':
            return new CampaignEmbeddedMessage();
        case 'modal':
            return new CampaignModal();
        case 'banner':
            return new CampaignBanner();
        case 'overlaybanner':
            return new CampaignOverlayBanner();
        case 'overlaychild':
            return new CampaignOverlayChild();
        case 'opxhost':
            return new CampaignOpxHost();
        case 'snackbar':
            return new CampaignSnackBar();
        case 'tooltip':
        default:
            return new CampaignTooltip();
    }
};
